import { ChevronDown, MessageSquareText } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { useConversationNavigation } from "@/hooks/use-conversation-navigation";
import type { ConversationTarget } from "@/lib/conversation-target";
import { conversationDestinationHref } from "@/lib/conversation-target";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { ReactNode } from "react";

type RailLayout = {
  expanded: boolean;
  panelId: string;
  renderPoint: (turn: ReturnType<typeof useConversationNavigation>["turns"][number] | null, position: number, active: boolean) => ReactNode;
};

function targetIsActive(active: ConversationTarget | null, target: ConversationTarget): boolean {
  if (!active || active.kind !== target.kind) return false;
  if (active.kind === "turn" && target.kind === "turn") return active.turnId === target.turnId;
  if (active.kind === "message" && target.kind === "message") {
    return active.messageId === target.messageId && active.headingKey === target.headingKey;
  }
  return active.kind === "thread" && target.kind === "thread" && active.threadId === target.threadId;
}

function turnIsActive(active: ConversationTarget | null, turn: ReturnType<typeof useConversationNavigation>["turns"][number]): boolean {
  if (!active) return false;
  if (targetIsActive(active, turn.primaryTarget) || targetIsActive(active, { kind: "message", messageId: turn.startMessageId })) return true;
  if (active.kind === "turn") return active.turnId === turn.turnId;
  if (active.kind === "thread") return turn.threads.some(({ child }) => child.id === active.threadId);
  return turn.replies.some(({ messageId }) => messageId === active.messageId)
    || turn.headings.some(({ message_id }) => message_id === active.messageId);
}

export function ConversationTimeline({
  sessionId,
  navigation,
  onNavigate,
  onOpenThread,
  rail,
}: {
  sessionId: string;
  navigation: ReturnType<typeof useConversationNavigation>;
  onNavigate: (target: ConversationTarget) => void;
  onOpenThread: (threadId: string) => void;
  rail?: RailLayout;
}) {
  const { t, i18n } = useTranslation();
  const activeRowClass = "bg-accent text-accent-foreground ring-1 ring-inset ring-border";
  // The navigation hook returns oldest-first turns, matching transcript reading order.
  const orderedTurns = navigation.turns;
  const contentCell = (content: ReactNode, className?: string) => rail ? (
    <div
      aria-hidden={!rail.expanded}
      ref={(element) => {
        if (!element) return;
        if (rail.expanded) element.removeAttribute("inert");
        else element.setAttribute("inert", "");
      }}
      className={cn("col-start-1 row-start-1 min-w-0 px-5", className, !rail.expanded && "pointer-events-none")}
    >{content}</div>
  ) : content;
  const auxiliaryRow = (content: ReactNode, className?: string, point?: ReactNode) => rail ? (
    <div className={cn("grid grid-cols-[20rem_2.5rem]", className)}>
      {point ?? <div aria-hidden="true" className="col-start-2 row-start-1" />}
      {contentCell(content)}
    </div>
  ) : className ? <div className={className}>{content}</div> : content;
  const loadEarlierControl = navigation.hasMoreTurns && auxiliaryRow(
    <Button variant="ghost" size="sm" className={cn("timeline-interactive h-auto min-h-9 w-full whitespace-normal px-3 py-2 text-sm font-normal text-muted-foreground", rail ? "mb-6" : "mt-6")} disabled={navigation.loadingOlderTurns} onClick={navigation.loadMoreTurns}>
      {navigation.loadingOlderTurns ? t("navigation.timelineLoading") : t("navigation.loadMoreTurns")}
    </Button>
  );
  const follow = (target: ConversationTarget) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onNavigate(target);
  };
  const targetLink = (target: ConversationTarget, label: React.ReactNode, className?: string) => (
    <a
      href={conversationDestinationHref({ sessionId, target })}
      onClick={follow(target)}
      aria-current={targetIsActive(navigation.activeTarget, target) ? "location" : undefined}
      className={cn(
        "timeline-interactive -mx-2 rounded-sm px-2 text-left text-sm leading-relaxed",
        targetIsActive(navigation.activeTarget, target) ? activeRowClass : "hover:bg-accent/50 focus-visible:bg-accent/50",
        className,
      )}
    >
      {label}
    </a>
  );

  return (
    <nav id={rail?.panelId} aria-label={t("navigation.timelineLabel")} className={cn("conversation-timeline-theme min-w-0 bg-background font-sans text-foreground", rail ? "w-[22.5rem] pt-6 pb-6" : "p-5")}>
      {!rail && auxiliaryRow(<div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold leading-6">{t("navigation.timeline")}</h2>
        {(navigation.capabilityState === "pending" || navigation.loadingChildren) && (
          <span className="text-xs leading-5 text-muted-foreground" role="status">{t("navigation.timelineLoading")}</span>
        )}
      </div>, "sticky top-0 z-20 -mx-5 mb-6 border-b border-border/60 bg-background px-5 pb-4")}
      {rail && navigation.turns.length === 0 && auxiliaryRow(null, undefined, rail.renderPoint(null, 0, false))}

      {(navigation.capabilityState === "unsupported"
        || navigation.capabilityState === "error"
        || (navigation.capabilityState === "supported" && !navigation.indexSupported)) && (
        auxiliaryRow(<p className="mb-6 border-l-2 border-border pl-3 text-sm leading-relaxed text-muted-foreground" role="status">
          {t("navigation.headingsUnavailable")}
        </p>)
      )}
      {navigation.indexState === "missing" && (
        auxiliaryRow(<p className="mb-6 border-l-2 border-border pl-3 text-sm leading-relaxed text-muted-foreground" role="status">{t("navigation.headingsNotIndexed")}</p>)
      )}
      {(navigation.indexState === "partial" || navigation.indexState === "building") && (
        auxiliaryRow(<p className="mb-6 border-l-2 border-border pl-3 text-sm leading-relaxed text-muted-foreground" role="status">{t("navigation.partialCoverage")}</p>)
      )}
      {(navigation.indexError || navigation.indexState === "error") && (
        auxiliaryRow(<div className="mb-6 border-l-2 border-destructive/50 pl-3 text-sm leading-relaxed text-destructive" role="alert">
          {t("navigation.indexLoadError")}{" "}
          <button type="button" className="timeline-interactive underline underline-offset-4" onClick={navigation.retryIndex}>{t("common:actions.retry")}</button>
        </div>)
      )}

      {navigation.turns.length === 0 && navigation.otherThreads.length === 0 && !navigation.loadingChildren && (
        auxiliaryRow(<p className="mb-6 text-sm leading-relaxed text-muted-foreground">{t("navigation.timelineEmpty")}</p>)
      )}

      {rail && loadEarlierControl}
      <ol className={rail ? "" : "divide-y divide-border/60"}>
        {orderedTurns.map((turn, turnPosition) => {
          // This index is chronological in both the rail and dialog.
          const turnNumber = turn.ordinal ?? turnPosition + 1;
          const turnTarget = turn.primaryTarget;
          const date = turn.startedAt ? new Date(turn.startedAt) : null;
          const time = date && !Number.isNaN(date.valueOf())
            ? new Intl.DateTimeFormat(i18n.language, { hour: "numeric", minute: "2-digit" }).format(date)
            : null;
          // The connector starts at the first (oldest) dot and ends at the last
          // (newest) dot; the load-earlier row sits outside this list above it.
          const active = turnIsActive(navigation.activeTarget, turn);
          return (
            <li key={turn.turnId} data-testid={rail ? "timeline-turn-row" : undefined} data-timeline-active={rail && active ? "true" : undefined} className={cn("min-w-0 py-4 first:pt-0 last:pb-0", rail && "relative grid grid-cols-[20rem_2.5rem] before:absolute before:inset-y-0 before:left-[21.25rem] before:w-px before:bg-border/60 first:before:top-3.5 last:before:bottom-auto last:before:h-[1.875rem] only:before:hidden")}>
              {rail && rail.renderPoint(turn, turnPosition, active)}
              {contentCell(<>
              {!rail && <span className="sr-only">{t("navigation.turnPoint", { count: turnNumber })}</span>}
              <details open={turn.loaded || turn.threads.length > 0}>
                <summary className="timeline-interactive relative flex cursor-pointer list-none items-baseline gap-2 [&::-webkit-details-marker]:hidden">
                  <ChevronDown className="absolute -left-4 top-2 size-3 text-muted-foreground transition-transform motion-reduce:transition-none [[open]>&]:rotate-180" aria-hidden="true" />
                  {targetLink(
                    turnTarget,
                    <>
                      <span className="text-sm font-semibold leading-6">{t("navigation.turn", { count: turnNumber })}</span>
                      {time && <span className="font-sans text-xs font-normal leading-5 text-muted-foreground"> · {time}</span>}
                    </>,
                    "flex min-w-0 flex-wrap items-baseline gap-x-2",
                  )}
                  {turn.pending && <span className="text-xs leading-5 text-muted-foreground">{t("navigation.pending")}</span>}
                </summary>
                <div className="mt-3 space-y-4 pl-5">
                  {!turn.loaded && (
                    <div>{targetLink({ kind: "turn", turnId: turn.turnId }, t("navigation.loadReplies"), "inline-block py-1")}</div>
                  )}
                  {turn.replies.length > 0 && (
                    <ol className="space-y-2" aria-label={t("navigation.replies") }>
                      {turn.replies.map((reply, index) => (
                        <li key={reply.messageId} className="min-w-0">
                          {targetLink(
                            { kind: "message", messageId: reply.messageId },
                            reply.label || t("navigation.reply", { count: index + 1 }),
                            "block break-words py-1",
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                  {turn.headings.length > 0 && (
                    <ol className="space-y-2" aria-label={t("navigation.headings") }>
                      {turn.headings.map((heading) => (
                        <li key={`${heading.message_id}:${heading.key}`} style={{ paddingInlineStart: `${Math.max(0, heading.level - 1) * 0.5}rem` }}>
                          {targetLink(
                            { kind: "message", messageId: heading.message_id, headingKey: heading.key },
                            heading.text || t("navigation.untitledHeading"),
                            "block break-words py-1 text-sm leading-relaxed",
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                  {turn.headingsNextCursor && (
                    <Button variant="ghost" size="sm" className="timeline-interactive h-auto min-h-8 justify-start whitespace-normal px-3 py-2 text-left text-xs font-normal text-muted-foreground" onClick={() => navigation.loadMoreHeadings(turn.turnId)}>
                      {t("navigation.loadMoreHeadings")}
                    </Button>
                  )}
                  {turn.threads.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs leading-5 text-muted-foreground">{t("navigation.threads", { count: turn.threads.length })}</p>
                      <ol className="divide-y divide-border/60 border-l-2 border-border pl-3">
                        {turn.threads.map((thread) => (
                          <li key={thread.child.id} className="py-3 first:pt-0 last:pb-0">
                            <button type="button" aria-current={navigation.activeTarget?.kind === "thread" && navigation.activeTarget.threadId === thread.child.id ? "location" : undefined} className={cn("timeline-interactive -mx-2 flex w-full items-start gap-2 rounded-sm px-2 text-left text-sm leading-relaxed", navigation.activeTarget?.kind === "thread" && navigation.activeTarget.threadId === thread.child.id ? activeRowClass : "hover:bg-accent/50")} onClick={() => onOpenThread(thread.child.id)}>
                              <MessageSquareText className="mt-1 size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
                              <span className="min-w-0">
                                <span className="block break-words">{thread.child.title}</span>
                                <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">{t("navigation.openThread")}</span>
                              </span>
                            </button>
                            {thread.sourceMessageId && (
                              <div className="mt-2">
                                {targetLink(
                                  { kind: "message", messageId: thread.sourceMessageId },
                                  t("navigation.showOrigin"),
                                  "inline-block py-1 text-xs text-muted-foreground",
                                )}
                              </div>
                            )}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </details>
              </>)}
            </li>
          );
        })}
      </ol>

      {!rail && loadEarlierControl}

      {navigation.otherThreads.length > 0 && auxiliaryRow(
        <section className="mt-6 border-t border-border/60 pt-6">
          <h3 className="mb-4 text-sm font-semibold leading-6">{t("navigation.otherThreads")}</h3>
          <ol className="divide-y divide-border/60 border-l-2 border-border pl-3">
            {navigation.otherThreads.map((thread) => (
              <li key={thread.child.id} className="py-3 first:pt-0 last:pb-0">
                <button type="button" aria-current={navigation.activeTarget?.kind === "thread" && navigation.activeTarget.threadId === thread.child.id ? "location" : undefined} className={cn("timeline-interactive -mx-2 w-full rounded-sm px-2 text-left text-sm leading-relaxed", navigation.activeTarget?.kind === "thread" && navigation.activeTarget.threadId === thread.child.id ? activeRowClass : "hover:bg-accent/50")} onClick={() => onOpenThread(thread.child.id)}>
                  <span className="block break-words">{thread.child.title}</span>
                  <span className="mt-1 block text-xs leading-5 text-muted-foreground">{t("navigation.openThread")}</span>
                </button>
                {thread.sourceMessageId && (
                  <span className="mt-2 block">
                    {targetLink({ kind: "message", messageId: thread.sourceMessageId }, t("navigation.showOrigin"), "inline-block py-1 text-xs text-muted-foreground")}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {navigation.childrenError && auxiliaryRow(
        <div className="mt-6 border-l-2 border-destructive/50 pl-3 text-sm leading-relaxed text-destructive" role="alert">
          {t("navigation.threadsLoadError")}{" "}
          <button type="button" className="timeline-interactive underline underline-offset-4" onClick={navigation.retryChildren}>{t("common:actions.retry")}</button>
        </div>
      )}
      {navigation.hasMoreThreads && auxiliaryRow(
        <Button variant="ghost" size="sm" className="timeline-interactive mt-4 h-auto min-h-9 w-full whitespace-normal px-3 py-2 text-sm font-normal text-muted-foreground" disabled={navigation.loadingMoreThreads} onClick={navigation.loadMoreThreads}>
          {navigation.loadingMoreThreads ? t("navigation.timelineLoading") : t("navigation.loadMoreThreads")}
        </Button>
      )}
    </nav>
  );
}
